<template lang="pug">
    v-dialog(v-model='dialog' scrollable persistent max-width='750px')
        template(v-slot:activator='{ on, attrs }')
            v-tooltip(bottom)
                template(v-slot:activator='{ on: onTooltip, attrs: attrsTooltip }')
                    v-icon.beacon(v-bind="attrsTooltip" v-on="onTooltip" @click="dialog = true" :class="beaconClass")
                        | mdi-power-plug
                span {{ tooltipText }}

        LoginForm(
            :auth-event="authEvent"
            :current-user="currentUser"
            :vin-login-status="status"
            @close-dialog="closeDialog"
        )

</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components
import LoginForm from '@/components/integration_app/login_form.vue';

// Data
import { GetIntegrationUser } from '@/data';

export default Vue.extend({
    data() {
        return {
            authEvent: null,
            bus: VolieState.websocketClient().eventBus(),
            dialog: false,
            status: "unenabled",
        }
    },
    methods: {
        closeDialog() {
            this.dialog = false;
        },
        getIntegrationUserStatus() {
            if (!this.currentUser?.need_vin_solutions_auth || !this.currentUser?.vin_login_enabled) {
                this.status = "unenabled";
                return;
            }

            this.applyVinLoginEnforcementStatus();
        },
        applyVinLoginEnforcementStatus() {
            const enforcement = this.currentUser?.vin_login_enforcement;
            if (enforcement?.enabled === true) {
                this.status = enforcement.has_active_session ? "online" : "offline";
                return;
            }

            this.getIntegrationUserLoginStatus();
        },
        getIntegrationUserLoginStatus() {
            GetIntegrationUser().then((response) => {
                if (response.Error) {
                    console.error("getIntegrationUserLoginStatus: ", response.Error);
                    return;
                }

                const user = response.Data?.user;
                if (!user) {
                    this.status = "unenabled";
                    return;
                }

                if (user.message == "User not found") {
                    this.status = "offline";
                    return;
                }

                const logins = user.logins;
                if (!Array.isArray(logins)) {
                    this.status = "unenabled";
                    return;
                }

                if (logins.length === 0) {
                    this.status = "offline";
                    return;
                }

                this.status = logins[0].login_successful ? "online" : "offline";
            });
        },
        onAuthEventNew(response) {
            // Verify response
            const authEvent = response?.Data;

            if (!authEvent) {
                return;
            }

            this.authEvent = authEvent;

            switch(authEvent) {
                case "auth.failure":
                case "auth.mfa_required":
                    this.status = "offline";
                    if (this.shouldOpenLoginDialog) {
                        this.dialog = true;
                    }
                    break;
                case "auth.timeout":
                case "auth.error":
                case "session.ended":
                    this.status = "offline";
                    break;
                case "auth.success":
                case "session.started":
                    this.status = "online";
                    break;
                default:
                    break;
            }
        },
        openVinLoginDecisionDialog() {
            this.applyVinLoginEnforcementStatus();
            if (this.shouldOpenLoginDialog) {
                this.dialog = true;
            }
        },
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
        beaconClass() {
            return {
                'offline': this.status === 'offline',
                'unenabled': this.status === 'unenabled',
                'online': this.status === 'online'
            };
        },
        tooltipText(): string {
            switch (this.status) {
                case "online":
                    return "CRM Integration is online";
                case "offline":
                    return "CRM Integration is offline";
                case "unenabled":
                default:
                    return "CRM Integration is not enabled";
            }
        },
        shouldOpenLoginDialog() {
            const enforcement = this.currentUser?.vin_login_enforcement;
            return this.currentUser?.need_vin_solutions_auth === true && this.currentUser?.vin_login_enabled === true && enforcement?.manual_entry_required !== true;
        },
        vinLoginStatusKey() {
            const enforcement = this.currentUser?.vin_login_enforcement || {};
            return [
                this.currentUser?.need_vin_solutions_auth === true,
                this.currentUser?.vin_login_enabled === true,
                enforcement.enabled === true,
                enforcement.has_active_session === true,
            ].join("|");
        },
    },
    components: {
        LoginForm,
    },
    props: {
    },
    watch: {
        vinLoginStatusKey() {
            this.getIntegrationUserStatus();
        },
    },
    created() {
        this.getIntegrationUserStatus();

        this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "integrationOnIntegrationAuth" }}, this.onAuthEventNew);
        this.bus.subscribe({ type: "vinSolutionsLoginDecisionRequired" }, this.openVinLoginDecisionDialog);
    },
    destroyed() {
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "integrationOnIntegrationAuth" }}, this.onAuthEventNew);
        this.bus.unsubscribe({ type: "vinSolutionsLoginDecisionRequired" }, this.openVinLoginDecisionDialog);
    },
    mounted() {
    },
})
</script>

<style lang="scss" scoped>
 #app {
    .beacon {
        cursor: pointer;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: radial-gradient(#818181, #979797);
        box-shadow: 0px 0px 5px #979797;
        display: block;
        z-index: 1000;
        animation: none;
        &.offline {
            background: radial-gradient(#f23434, #ff5252);
            box-shadow: 0px 0px 5px #ff5252;
            animation: pulse-offline 1s infinite;
        }
        &.unenabled {
            background: radial-gradient(#818181, #979797);
            box-shadow: 0px 0px 5px #979797;
            animation: none;
        }
        &.online {
            background: radial-gradient(#52d0d2, #48C1C3);
            box-shadow: 0px 0px 5px #48C1C3;
            animation: pulse 2s infinite;
        }
    }
 }

 @keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes pulse-offline {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
}
</style>
